import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/Layout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
            alt="Keder Screens"
            className="h-16 mx-auto mb-4"
          />
          <p className="text-gray-400 mt-2">Dealer Portal</p>
        </div>

        {/* Card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-2">Reset your password</h2>
          <p className="text-gray-400 text-sm mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {success ? (
            <div className="text-center">
              <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-4 rounded-lg mb-6">
                <p className="font-medium">Check your email</p>
                <p className="text-sm mt-1">
                  If an account exists with that email, you'll receive a password reset link shortly.
                </p>
              </div>
              <Link to="/login" className="text-keder-yellow hover:underline">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="label">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="dealer@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Sending...
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-gray-400 hover:text-keder-yellow">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-8">
          © {new Date().getFullYear()} Keder Screens. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
